import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackBar from '../components/BackBar';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => {
  const token = localStorage.getItem('opwmp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DWELLING_TYPES = [
  { key: '1bed', label: '1 Bed' },
  { key: '2bed', label: '2 Bed' },
  { key: '3bed', label: '3 Bed+' },
  { key: 'individual', label: 'Individual' },
];
const STREAMS = ['general', 'recycling', 'fogo', 'glass'];

const AdminCalculatorRates = () => {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterState, setFilterState] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState('');

  const fetchRates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/calculator-rates`, { headers: getAuthHeader() });
      setRates(res.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const openEdit = (council) => {
    setEditing(council.council_name);
    setEditData({
      rates: JSON.parse(JSON.stringify(council.rates || {})),
      collection_frequency: { ...council.collection_frequency },
      has_glass_stream: council.has_glass_stream || false,
      has_fogo: council.has_fogo || false,
      source: council.source || '',
      verified: council.verified || false,
      notes: council.notes || '',
    });
    // Init empty dwelling types if missing
    DWELLING_TYPES.forEach(dt => {
      if (!council.rates?.[dt.key]) {
        setEditData(prev => ({
          ...prev,
          rates: { ...prev.rates, [dt.key]: {} }
        }));
      }
    });
  };

  const updateRate = (dwellingKey, stream, value) => {
    setEditData(prev => ({
      ...prev,
      rates: {
        ...prev.rates,
        [dwellingKey]: {
          ...prev.rates[dwellingKey],
          [stream]: value === '' ? undefined : parseFloat(value),
        }
      }
    }));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      // Clean: remove undefined/NaN values
      const cleanRates = {};
      for (const dt of DWELLING_TYPES) {
        cleanRates[dt.key] = {};
        for (const s of STREAMS) {
          const v = editData.rates?.[dt.key]?.[s];
          if (v !== undefined && v !== null && !isNaN(v)) {
            cleanRates[dt.key][s] = v;
          }
        }
      }
      await axios.put(`${API}/api/admin/calculator-rates/${editing}`, {
        ...editData,
        rates: cleanRates,
      }, { headers: getAuthHeader() });
      setMessage(`Saved ${editing}`);
      setEditing(null);
      setEditData(null);
      fetchRates();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(`Error: ${err.response?.data?.detail || err.message}`);
    }
    setSaving(false);
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/calculator-rates-export`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'calculator_rates.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage('Export failed');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/admin/calculator-rates-import`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setMessage(`Imported: ${res.data.created} created, ${res.data.updated} updated${res.data.errors?.length ? `, ${res.data.errors.length} errors` : ''}`);
      fetchRates();
    } catch (err) {
      setMessage('Import failed');
    }
    setImporting(false);
    e.target.value = '';
  };

  const getRateValue = (council, dwellingKey, stream) => {
    return council.rates?.[dwellingKey]?.[stream];
  };

  const hasAnyRates = (council) => {
    return DWELLING_TYPES.some(dt =>
      STREAMS.some(s => getRateValue(council, dt.key, s) !== undefined && getRateValue(council, dt.key, s) !== null)
    );
  };

  const filtered = rates.filter(r => {
    if (filterState && r.state !== filterState) return false;
    if (filterVerified === 'yes' && !r.verified) return false;
    if (filterVerified === 'no' && r.verified) return false;
    if (filterVerified === 'empty' && hasAnyRates(r)) return false;
    return true;
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="calculator-rates-page">
      <BackBar to="/admin" label="Admin" />

      <header className="bg-white shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Generation Rates Database</h1>
              <p className="text-sm text-gray-500">{rates.length} councils ({rates.filter(r => r.state === 'NSW').length} NSW, {rates.filter(r => r.state === 'VIC').length} VIC)</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleExport} className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-testid="export-csv-btn">
                Export CSV
              </button>
              <label className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                {importing ? 'Importing...' : 'Import CSV'}
                <input type="file" accept=".csv" onChange={handleImport} className="hidden" disabled={importing} data-testid="import-csv-input" />
              </label>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {message && (
          <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${message.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {message}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <select value={filterState} onChange={e => setFilterState(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
            <option value="">All States</option>
            <option value="NSW">NSW</option>
            <option value="VIC">VIC</option>
          </select>
          <select value={filterVerified} onChange={e => setFilterVerified(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
            <option value="">All Status</option>
            <option value="yes">Verified</option>
            <option value="no">Unverified</option>
            <option value="empty">No Rates</option>
          </select>
          <span className="text-sm text-gray-500 py-1.5">{filtered.length} shown</span>
        </div>

        {/* Rates Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10 min-w-[180px]">Council</th>
                <th className="px-2 py-2 text-center font-medium text-gray-600 w-10">St</th>
                {DWELLING_TYPES.map(dt => (
                  STREAMS.map(s => (
                    <th key={`${dt.key}-${s}`} className="px-1.5 py-2 text-center font-medium text-gray-500 text-[10px] leading-tight w-[52px]">
                      <div className="text-gray-700">{dt.label}</div>
                      <div className="text-gray-400 font-normal">{s.charAt(0).toUpperCase() + s.slice(0, 3)}</div>
                    </th>
                  ))
                ))}
                <th className="px-2 py-2 text-center font-medium text-gray-600 w-12">
                  <svg className="w-4 h-4 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </th>
                <th className="px-2 py-2 text-center font-medium text-gray-600 w-16">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(council => {
                const isEmpty = !hasAnyRates(council);
                return (
                  <tr key={council.council_name} className={`${isEmpty ? 'bg-amber-50/40' : council.verified ? 'bg-green-50/30' : ''} hover:bg-blue-50/30 transition-colors`}>
                    <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-inherit z-10 whitespace-nowrap">
                      {council.council_name}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${council.state === 'NSW' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {council.state}
                      </span>
                    </td>
                    {DWELLING_TYPES.map(dt =>
                      STREAMS.map(s => {
                        const val = getRateValue(council, dt.key, s);
                        return (
                          <td key={`${dt.key}-${s}`} className="px-1.5 py-2 text-center text-xs tabular-nums">
                            {val !== undefined && val !== null ? (
                              <span className="text-gray-700">{val}</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        );
                      })
                    )}
                    <td className="px-2 py-2 text-center">
                      {council.verified ? (
                        <span className="text-green-500 text-xs font-bold">Yes</span>
                      ) : (
                        <span className="text-gray-300 text-xs">No</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => openEdit(council)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>

      {/* Edit Modal */}
      {editing && editData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen px-4 pt-8 pb-20">
            <div className="fixed inset-0 bg-gray-900/75" onClick={() => { setEditing(null); setEditData(null); }} />
            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full z-10">
              <div className="flex justify-between items-center p-5 border-b">
                <h2 className="text-lg font-bold text-gray-900">{editing}</h2>
                <button onClick={() => { setEditing(null); setEditData(null); }} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
                {/* Rates Grid */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2">Generation Rates (L/unit/week)</h3>
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-600">Dwelling</th>
                        <th className="px-2 py-2 text-center text-gray-600">General</th>
                        <th className="px-2 py-2 text-center text-gray-600">Recycling</th>
                        <th className="px-2 py-2 text-center text-gray-600">FOGO</th>
                        <th className="px-2 py-2 text-center text-gray-600">Glass</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {DWELLING_TYPES.map(dt => (
                        <tr key={dt.key}>
                          <td className="px-3 py-2 font-medium text-gray-700">{dt.label}</td>
                          {STREAMS.map(s => (
                            <td key={s} className="px-1 py-1">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={editData.rates?.[dt.key]?.[s] ?? ''}
                                onChange={e => updateRate(dt.key, s, e.target.value)}
                                className="w-full px-2 py-1.5 text-center text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                placeholder="—"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Options */}
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={editData.has_fogo} onChange={e => setEditData({ ...editData, has_fogo: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Has FOGO stream
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={editData.has_glass_stream} onChange={e => setEditData({ ...editData, has_glass_stream: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Has Glass stream
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 col-span-2">
                    <input type="checkbox" checked={editData.verified} onChange={e => setEditData({ ...editData, verified: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                    Verified (rates confirmed from completed WMP)
                  </label>
                </div>

                {/* Source & Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Source / DCP Reference</label>
                  <input type="text" value={editData.source} onChange={e => setEditData({ ...editData, source: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/30" placeholder="e.g., Sydney DCP 2012 Section 3.14" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Notes</label>
                  <textarea value={editData.notes} onChange={e => setEditData({ ...editData, notes: e.target.value })} rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/30 resize-none" placeholder="Any notes about these rates..." />
                </div>
              </div>
              <div className="flex justify-end gap-3 p-5 border-t">
                <button onClick={() => { setEditing(null); setEditData(null); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                <button onClick={saveEdit} disabled={saving} className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Rates'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCalculatorRates;
